import * as React from "react";
import { Grid, SxProps, Theme } from "@mui/material";

interface GridWrapperProps {
  children: React.ReactNode;
  containerSx?: SxProps<Theme>;
  contentSx?: SxProps<Theme>;
  leftGutterSx?: SxProps<Theme>;
  rightGutterSx?: SxProps<Theme>;
  spacing?: number;
  columns?: number;
}

/**
 * Reusable Grid Wrapper Component
 * 
 * Provides a consistent grid layout pattern used across the app:
 * - Full width on mobile (xs: 12)
 * - 8 columns on tablet (sm: 8) with 2 column gutters
 * - 4 columns on desktop (md: 4) with 4 column gutters
 * 
 * @param children - Content to be placed in the main content area
 * @param containerSx - Custom styles for the Grid container
 * @param contentSx - Custom styles for the main content Grid
 * @param leftGutterSx - Custom styles for the left gutter Grid
 * @param rightGutterSx - Custom styles for the right gutter Grid
 * @param spacing - Grid spacing (default: 0.5)
 * @param columns - Total columns (default: 12)
 */
const GridWrapper: React.FC<GridWrapperProps> = ({
  children,
  containerSx,
  contentSx,
  leftGutterSx,
  rightGutterSx,
  spacing = 0.5,
  columns = 12,
}) => {
  return (
    <Grid
      container
      spacing={spacing}
      columns={columns}
      sx={{
        minWidth: "320px",
        width: "100%",
        maxWidth: "100vw",
        overflow: "hidden",
        ...containerSx,
      }}
    >
      {/* Left gutter: hidden on xs, 2 cols on sm, 4 cols on md+ */}
      <Grid
        size={{ sm: 2, md: 4 }}
        sx={{
          // backgroundColor:'red',
          display: { xs: "none", sm: "block" },
          minWidth: { sm: "auto" },
          ...leftGutterSx,
        }}
      />

      {/* Main content: full width on xs, 8 cols on sm, 4 cols on md */}
      <Grid
        size={{ xs: 12, sm: 8, md: 4 }}
        sx={{
          minWidth: { xs: "280px", sm: "auto" },
          width: "100%",
          maxWidth: "100%",
          ...contentSx,
        }}
      >
        {children}
      </Grid>

      {/* Right gutter: mirror of left */}
      <Grid
        size={{ sm: 2, md: 4 }}
        sx={{
          // backgroundColor:'red',
          display: { xs: "none", sm: "block" },
          minWidth: { sm: "auto" },
          ...rightGutterSx,
        }}
      />
    </Grid>
  );
};

export default GridWrapper;

